import { Component, ViewEncapsulation, OnInit, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl } from '@angular/forms';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { ToDosQueriesService } from 'src/shared/services/to-dos-queries.service';

import { CurrentAddress } from 'src/providers/addresses/current-address';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Util } from 'src/shared/util/util';
import { Events } from 'src/providers/events/events';

import { Loading } from 'src/shared/components/loading/loading';

import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';
import {
  CHECKLIST_TEMPLATES,
  ChecklistTemplate,
  TemplateItem,
  getTemplateCategories,
} from 'src/shared/constants/checklist-templates';

@Component({
    templateUrl: 'add-list.html',
    styleUrls: ['add-list.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class ToDosAddListPage implements OnInit {

  addressId: number;
  addressFilter: TidySelectNumberValueModel[];
  allToDolists: any;
  default = false;
  errorMessage: string;
  form: UntypedFormGroup;
  hasToDoLists: boolean;
  isRightSideContent: boolean;
  cameFromOnboarding: boolean;
  listToCopyItems: any;
  loaded: boolean;
  submitted: boolean;
  toDoLists: any;
  remoteInspection = false;
  readonly typeItems = [
    {
      viewValue: 'Photos',
      value: 'photo'
    },
    {
      viewValue: 'Videos',
      value: 'video'
    }
  ];
  readonly whenItems = [
    {
      viewValue: 'Before and after the job',
      value: 'before_and_after'
    },
    {
      viewValue: 'Before the job',
      value: 'before'
    },
    {
      viewValue: 'After the job',
      value: 'after'
    }
  ];
  readonly whatToDoItems = [
    {
      viewValue: 'Add new list',
      value: 'new'
    },
    {
      viewValue: 'Copy list from another address',
      value: 'copy'
    }
  ];

  templates: ChecklistTemplate[] = CHECKLIST_TEMPLATES;
  templateCategories: string[] = getTemplateCategories();
  isCreatingFromTemplate = false;

  boundAddList = this.addList.bind(this);

  private readonly toDosQueries = inject(ToDosQueriesService);

constructor(
    private fb: UntypedFormBuilder,
    private navCtrl: CustomNavController,
    private storage: TidyStorage,
    private toDos: ToDos,
    private currentAddress: CurrentAddress,
    private rightSidePanelService: RightSidePanelService,
    private onboardingProvider: OnboardingProvider,
    private util: Util,
    private events: Events
  ) {
    this.form = this.fb.group({
      listName: ['', Validators.required],
      listDescription: [''],
      address: ['', Validators.required],
      whatToDo: ['', Validators.required],
      type: ['', this.remoteInspectionValidator.bind(this)],
      when: ['', this.remoteInspectionValidator.bind(this)],
      listToCopy: [''],
    });
  }

  @Loading('', true)
  async ngOnInit() {
    this.loaded = false;
    this.submitted = false;
    this.default = false;
    this.remoteInspection = false;
    this.errorMessage = '';
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    await this.getParams();
    this.form.reset();
    this.form.patchValue({
      address: [this.addressId],
      whatToDo: 'new'
    });
    //this.allToDolists = await this.toDos.getAllIsCustomerToDoLists();
    //this.listToCopyItems = this.buildListToCopyItems();
    this.loaded = true;
  }

  changeAddress() {
    this.listToCopyItems = this.buildListToCopyItems();
    this.form.patchValue({listToCopy: null});
  }

  buildListToCopyItems() {
    const array: any = [];
    this.allToDolists.map((list) => {
      if (this.form.value.address !== list.address_id) {
        array.push(({
          value: list.id,
          viewValue: list.title
        }));
      }
    });
    return array;
  }

  async getParams() {
    const dialogParams = await this.storage.retrieve('dialog-params');
    this.hasToDoLists = this.navCtrl.getParam('hasToDoLists') || dialogParams?.hasToDoLists;
    this.addressId = parseInt(localStorage.getItem('addressId'));
    this.addressFilter = this.navCtrl.getParam('addressFilter') || dialogParams?.addressFilter;
    this.toDoLists = this.navCtrl.getParam('toDoLists') || dialogParams?.toDoLists;
    this.cameFromOnboarding = dialogParams?.cameFromOnboarding || false;
  }

  async addList() {
    this.submitted = true;
    if (!this.form.valid) {
      this.util.showError('Please complete all required fields.');
      return;
    }
    const payload = this.getPayload();
    const response = await this.toDos.createList(payload);
    if (this.default) {
      await this.setDefaultLists(response.id);
    }
    localStorage.setItem('taskListId', response.id);
    const addressId = this.addressId;
    this.currentAddress.addressId = addressId.toString();
    // Invalidate lists cache for the address
    this.toDosQueries.invalidateLists(addressId);
    // Notify background page to refresh
    this.events.publish('checklist:updated');
    await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
    if (this.cameFromOnboarding) {
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } else if (this.isRightSideContent) {
      this.rightSidePanelService.navigateTo('to-dos', {});
    } else {
      this.navCtrl.navigateForward('to-dos');
    }
  }

  getPayload () {
    const payload = {
      all_addresses: true,
      title: this.form.value.listName,
      before_after_photos_state: this.remoteInspectionSetting(),
      description: this.form.value.listDescription
    }
    return payload;
  }

  async setDefaultLists(taskListId) {
    const defaultPayload = {
      address_task_list_id: taskListId,
      settings: [{
        key: 'default_list.account',
        value: true
      }]
    }
    await this.toDos.saveDefaultList(taskListId, defaultPayload);
  }

  async copyListToAddress() {
    try {
      const payload = {
        target_address_id: this.form.value.address
      }
      await this.toDos.copyListToAddress(this.addressId, this.form.value.listToCopy, payload);
      localStorage.setItem('addressId', this.form.value.address);
      // Invalidate lists cache for the target address
      this.toDosQueries.invalidateLists(this.form.value.address);
      // Notify background page to refresh
      this.events.publish('checklist:updated');
      await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
      if (this.cameFromOnboarding) {
        if (this.isRightSideContent) {
          this.rightSidePanelService.closeRightSidePanel();
        } else {
          this.navCtrl.navigateForward('getting-started');
        }
      } else {
        this.navCtrl.navigateForward(this.isRightSideContent ? 'checklists' : 'to-dos');
      }
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      if (this.errorMessage === 'Action not allowed. The Task List has rooms associated!') {
        this.errorMessage = 'This list has rooms unique to the address in it, so it cannot be copied. Please remove the rooms or create a new list with no address specific rooms in it.'
      }
    }
  }

  updateWhatToDo(selection) {
    if (selection.value === 'new') {
      this.form.controls.listToCopy.clearValidators();
      this.form.controls.listToCopy.updateValueAndValidity();
      this.form.controls.listName.setValidators([Validators.required]);
    } else {
      this.form.controls.listToCopy.setValidators([Validators.required]);
      this.form.controls.listName.clearValidators();
      this.form.controls.listName.updateValueAndValidity();
    }
  }

  remoteInspectionSetting() {
    if (!this.remoteInspection) {
      return 'inactive';
    }

    const when = this.form.value.when;
    const type = this.form.value.type;

    if (when === 'before_and_after') {
      return type;
    }

    return `${when}_${type}_only`;
  }

  remoteInspectionValidator(formControl: AbstractControl) {
    if (this.remoteInspection) {
      return Validators.required(formControl);
    }

    return null;
  }

  toggleRemoteInspection() {
    this.remoteInspection = !this.remoteInspection;
    this.form.controls.type.updateValueAndValidity();
    this.form.controls.when.updateValueAndValidity();
  }

  goToListSettings() {
    this.navCtrl.navigateForward('list-settings');
  }

  getTemplatesByCategory(category: string): ChecklistTemplate[] {
    return this.templates.filter(t => t.category === category);
  }

  async selectTemplate(template: ChecklistTemplate) {
    if (this.isCreatingFromTemplate) {
      return;
    }
    this.isCreatingFromTemplate = true;

    try {
      const addressId = parseInt(localStorage.getItem('addressId') || '0', 10);

      // 1. Check for existing lists with the same name and increment if needed
      const existingLists = await this.toDosQueries.getListsForAddress(addressId);
      const uniqueTitle = this.getUniqueListTitle(template.name, existingLists);

      // 2. Create the list
      const listPayload = {
        all_addresses: true,
        title: uniqueTitle,
        before_after_photos_state: 'inactive',
        description: template.description,
      };
      const listResponse = await this.toDos.createList(listPayload);
      const taskListId = listResponse.id;
      localStorage.setItem('taskListId', taskListId);

      // 3. Create sections and items with limited concurrency (max 2 at a time)
      await this.processWithConcurrency(template.sections, async (section) => {
        const groupResponse = await this.toDos.addGroup({ name: section.name });
        const groupId = groupResponse.id;
        await this.toDos.updateGroup(groupId, { address_task_list_id: taskListId });

        await this.processWithConcurrency(section.items, async (item: TemplateItem) => {
          const itemPayload = {
            title: item.title,
            note: item.note || '',
            estimated_time: 0,
            is_important: item.is_important || false,
            task_category_options: [],
            address_task_group_ids: [groupId],
            address_id: addressId,
            address_task_list_id: taskListId,
            task_type: 'cleaning',
          };
          await this.toDos.addToDo(addressId, taskListId, itemPayload);
        }, 2);
      }, 2);

      // 4. Invalidate caches - list was created and populated
      this.toDosQueries.invalidateLists(addressId);
      this.toDosQueries.invalidateListDetail(taskListId);
      // Notify background page to refresh
      this.events.publish('checklist:updated');

      // 5. Mark onboarding as complete if applicable
      await this.onboardingProvider.markGoalAsCompleted('toDos');
      this.onboardingProvider.setShowOnboardingOnPage(false);
      this.onboardingProvider.setShowOnboardingInHeader(true);

      // 6. Navigate to the checklist page
      await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
      if (this.cameFromOnboarding) {
        if (this.isRightSideContent) {
          this.rightSidePanelService.closeRightSidePanel();
        } else {
          this.navCtrl.navigateForward('getting-started');
        }
      } else if (this.isRightSideContent) {
        this.rightSidePanelService.navigateTo('to-dos', {});
      } else {
        this.navCtrl.navigateForward('to-dos');
      }
    } catch (err) {
      this.util.showError((err as any)?.error?.message || (err as Error).message, 10000);
    } finally {
      this.isCreatingFromTemplate = false;
    }
  }

  private async processWithConcurrency<T>(
    items: T[],
    processor: (item: T) => Promise<void>,
    maxConcurrent: number
  ): Promise<void> {
    for (let i = 0; i < items.length; i += maxConcurrent) {
      const batch = items.slice(i, i + maxConcurrent);
      await Promise.all(batch.map(processor));
    }
  }

  private getUniqueListTitle(baseTitle: string, existingLists: any[]): string {
    const existingTitles = existingLists.map(list => list.title.toLowerCase());

    if (!existingTitles.includes(baseTitle.toLowerCase())) {
      return baseTitle;
    }

    let counter = 2;
    while (existingTitles.includes(`${baseTitle} ${counter}`.toLowerCase())) {
      counter++;
    }

    return `${baseTitle} ${counter}`;
  }
}
